/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { TouchScrollable } from 'react-scrolllock';

import { token } from '@atlaskit/tokens';

import { useModal } from './hooks';
import ScrollContainer from './internal/components/scroll-container';
import { keylineHeight } from './internal/constants';
import useScroll from './internal/hooks/use-scroll';

const bodyStyles = css({
	/* This ensures the body fills the whole space between header and footer. */
	flex: '1 1 auto',
});

/**
 * Adding the padding here avoids cropping the keyline on its sides.
 * The combined vertical spacing is maintained by subtracting the
 * keyline height from header and footer using negative margins.
 */
const bodyScrollStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	paddingBlock: keylineHeight,
	paddingInline: token('space.300'),
});

/**
 * Keylines will not be shown if scrolling in viewport so we do
 * not account for them in this case.
 */
const viewportScrollStyles = css({
	paddingInline: token('space.300'),
});

export interface ModalBodyProps {
	/**
	 * Children of modal dialog footer.
	 */
	children: React.ReactNode;

	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
}

/**
 * __Modal body__
 *
 * A modal body is used to display the main content of a modal.
 *
 * - [Examples](https://atlassian.design/components/modal-dialog/examples)
 * - [Code](https://atlassian.design/components/modal-dialog/code#modal-body-props)
 * - [Usage](https://atlassian.design/components/modal-dialog/usage)
 */
const ModalBody = (props: ModalBodyProps) => {
	const { children, testId: userDefinedTestId } = props;
	const { testId: modalTestId } = useModal();
	const shouldScrollInViewport = useScroll();

	const testId = userDefinedTestId || (modalTestId && `${modalTestId}--body`);

	return shouldScrollInViewport ? (
		<div css={[bodyStyles, viewportScrollStyles]} data-testid={testId}>
			{children}
		</div>
	) : (
		<TouchScrollable>
			<ScrollContainer testId={userDefinedTestId || modalTestId}>
				<div css={[bodyStyles, bodyScrollStyles]} data-testid={testId}>
					{children}
				</div>
			</ScrollContainer>
		</TouchScrollable>
	);
};

export default ModalBody;
